/* eslint-disable no-mixed-spaces-and-tabs */
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import moment from 'moment';
import { initialState } from './reducer';
import { FORM_NAME, TRAILER_TABLE_ROWS_NAMES, TYPES_WITH_DATE_ISSUE_AND_MODES } from './constants';
import { getLocalStorageItem } from '../../utils/localStorage';

export const selectTrailersModalDomain = state => state.trailersModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};
export const selectProfileDomain = state => state.profileModalReducer || {};

export const getTrailerInfoSelector = () =>
	createSelector(selectTrailersModalDomain, ({ trailerInfo }) => {
		if (!trailerInfo) {
			return [];
		}

		const modesNames = {};

		const modes = {};

		trailerInfo?.modes.forEach((el, index) => {
			modesNames[`mode${index + 1}`] = `${el.mode.name} °C`;
			modes[`mode${index + 1}`] = {
				validate: el?.is_validated ? 'Да' : 'Нет',
				date: el?.date ? moment(el?.date).format('DD.MM.YYYY') : 'Нет',
				...(el?.is_approved !== null ? { approve: el?.is_approved ? 'Да' : 'Нет' } : {}),
			};
			TRAILER_TABLE_ROWS_NAMES[`mode${index + 1}`] = `Температурный режим #${index + 1}`;
		});

		const data = {
			...(trailerInfo?.company
				? {
						company: {
							name: trailerInfo.company?.name || '-',
							role: trailerInfo.company?.type.name || '-',
							id: trailerInfo.company?.id || '',
						},
				  }
				: {}),
			...(trailerInfo?.legal
				? {
						legal: {
							name: trailerInfo.legal?.name || '-',
							inn: trailerInfo.legal?.inn || '-',
							id: trailerInfo.legal?.id || '',
						},
				  }
				: {}),
			number: trailerInfo.number || '-',
			vin: trailerInfo.vin || '-',
			type: trailerInfo.type?.name || '-',
			dateIssue: trailerInfo.date_issue ? moment(trailerInfo.date_issue).format('MM.YYYY') : '-',
			...(trailerInfo.type_date_issue
				? { typeDateIssue: moment(trailerInfo.type_date_issue).format('MM.YYYY') }
				: {}),
			weight: trailerInfo.weight ? `${trailerInfo.weight} тн` : '-',
			volume: trailerInfo.volume ? `${trailerInfo.volume} м³` : '-',
			ownership: trailerInfo.ownership?.name ? trailerInfo.ownership?.name : '-',
			...(trailerInfo.ownership && trailerInfo.ownership?.id !== 1 && trailerInfo.ownership?.id !== 2
				? {
						docName: trailerInfo.doc_name ? trailerInfo.doc_name : '-',
						docNumber: trailerInfo.doc_number ? trailerInfo.doc_number : '-',
						docDate: trailerInfo.doc_date ? moment(trailerInfo.doc_date).format('DD.MM.YYYY') : '-',
				  }
				: {}),
			...(trailerInfo.is_accredited !== null ? { isAccredited: trailerInfo.is_accredited ? 'Да' : 'Нет' } : {}),
			...modesNames,
			...(trailerInfo.user
				? {
						author: {
							name: `${trailerInfo.user?.last_name || '-'} ${trailerInfo.user?.first_name || '-'}`,
							role: trailerInfo.user?.role.name || '-',
						},
				  }
				: {}),
		};

		return Object.entries(data).reduce((result, [key, value]) => {
			return [...result, { id: key, title: TRAILER_TABLE_ROWS_NAMES[key], value, mode: modes[key] }];
		}, []);
	});

export const getIsTrailerInfoLoadingSelector = () =>
	createSelector(
		selectTrailersModalDomain,
		({ isTrailerInfoLoading, isTrailerDeleting, isTrailerSaving }) =>
			isTrailerInfoLoading || isTrailerDeleting || isTrailerSaving,
	);

export const getTrailerNameSelector = () =>
	createSelector(selectTrailersModalDomain, ({ trailerInfo }) => trailerInfo?.name || '');

export const getTrailerTypeIdSelector = () =>
	createSelector(selectTrailersModalDomain, ({ trailerInfo }) => trailerInfo?.type?.id);

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'trailer');
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'trailer' && query?.tab === 'audit');
	});

export const getIsEventTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'trailer' && query?.tab === 'event');
	});

export const getActiveTrailerIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => (query?.modal === 'trailer' ? query?.id : null));

export const getIsTrailerAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'trailer' && query?.mode === 'add');
	});

export const getIsTrailerEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'trailer' && Boolean(query?.id) && query?.mode === 'edit');
	});

export const getFormInitialValuesSelector = () =>
	createSelector(selectTrailersModalDomain, ({ trailerInfo }) => {
		if (!trailerInfo) {
			const companyId = getLocalStorageItem('user_data').companyId;

			return {
				ownership_id: null,
				modes: [{}],
				legal_id: companyId,
			};
		}

		return {
			legal_id: trailerInfo.legal?.id,
			number: trailerInfo.number,
			vin: trailerInfo.vin,
			name: trailerInfo.name,
			type_id: String(trailerInfo.type?.id),
			date_issue: trailerInfo.date_issue,
			type_date_issue: trailerInfo.type_date_issue,
			weight: trailerInfo.weight,
			volume: trailerInfo.volume,
			ownership_id: trailerInfo.ownership?.id ? trailerInfo.ownership?.id : null,
			...(trailerInfo.ownership && trailerInfo.ownership?.id !== 1 && trailerInfo.ownership?.id !== 2
				? {
						doc_name: trailerInfo.doc_name,
						doc_number: trailerInfo.doc_number,
						doc_date: trailerInfo.doc_date ? moment(trailerInfo.doc_date).format('YYYY-MM-DD') : '',
				  }
				: {}),
			is_accredited: trailerInfo.is_accredited,
			modes: trailerInfo.modes?.length
				? trailerInfo.modes?.map(mode => ({
						id: mode.id,
						mode_id: String(mode.mode?.id),
						is_validated: mode.is_validated,
						date: mode.date ? moment(mode.date).format('YYYY-MM-DD') : '',
				  }))
				: [{}],
		};
	});

export const getDataForCreateOrUpdateTrailerSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		const result = {
			legal_id: formValues.legal_id,
			name: formValues.name,
			number: formValues.number,
			vin: formValues.vin,
			type_id: formValues.type_id,
			date_issue: formValues.date_issue,
			weight: formValues.weight,
			volume: formValues.volume,
			ownership_id: formValues.ownership_id,
			doc_name: formValues.doc_name,
			doc_number: formValues.doc_number,
			doc_date: formValues.doc_date,
			is_accredited: formValues.is_accredited,
		};

		if (TYPES_WITH_DATE_ISSUE_AND_MODES.includes(formValues.type_id)) {
			result.type_date_issue = formValues.type_date_issue;

			const modes = formValues.modes?.reduce((acc, mode) => {
				if (!isEmpty(mode)) {
					acc.push({ ...mode, is_validated: Boolean(mode.is_validated) });
				}

				return acc;
			}, []);

			if (modes.length) {
				result.modes = modes;
			}
		}

		return result;
	});

export const getFormIsInvalidSelector = () => createSelector(isInvalid(FORM_NAME), isInvalid => isInvalid);

export const getOwnershipsSelector = () => createSelector(selectTrailersModalDomain, ({ ownerships }) => ownerships);

export const getLegalsSelector = () => createSelector(selectTrailersModalDomain, ({ legals }) => legals);

export const getIsLegalsLoadingSelector = () =>
	createSelector(selectTrailersModalDomain, ({ isLegalsLoading }) => isLegalsLoading);

export const getTrailerTypesSelector = () =>
	createSelector(selectTrailersModalDomain, ({ trailerTypes }) => trailerTypes);

export const getModesSelector = () => createSelector(selectTrailersModalDomain, ({ modes }) => modes);

export const getIsOwnershipsLoadingSelector = () =>
	createSelector(selectTrailersModalDomain, ({ isOwnershipsLoading }) => isOwnershipsLoading);

export const getIsOwnershipVisibleSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return formValues?.ownership_id && formValues?.ownership_id !== 1 && formValues.ownership_id !== 2;
	});

export const getIsTrailerTypesLoadingSelector = () =>
	createSelector(selectTrailersModalDomain, ({ isTrailerTypesLoading }) => isTrailerTypesLoading);

export const getIsModesLoadingSelector = () =>
	createSelector(selectTrailersModalDomain, ({ isModesLoading }) => isModesLoading);

export const getIsTypeDateIssueVisibleSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return TYPES_WITH_DATE_ISSUE_AND_MODES.includes(formValues?.type_id);
	});

export const getIsAdminSelector = () =>
	createSelector(selectProfileDomain, ({ userInfo }) => {
		return userInfo?.company?.id === 1;
	});

export const getTrailerTabsSelector = () =>
	createSelector(selectTrailersModalDomain, ({ trailerInfo }) => trailerInfo?.tabs || {});

export const getEventDataSelector = () => createSelector(selectTrailersModalDomain, ({ eventData }) => eventData);

export const getIsEventDataLoadingSelector = () =>
	createSelector(selectTrailersModalDomain, ({ isEventDataLoading }) => isEventDataLoading);

export const getIsAccreditationTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'trailer' && query?.tab === 'accreditation');
	});

export const getTrailerActionsSelector = () =>
	createSelector(selectTrailersModalDomain, ({ trailerInfo }) => trailerInfo?.actions || {});
